import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Box, Button, Grid, TextField, MenuItem } from "@mui/material";
import AppSwitch from "components/Shared/AppSwitch";

const weekdays = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const INITIAL_STATE = {
  scheduleType: "Fixed",
  day: "",
  date: "",
  startTime: "",
  endTime: "",
  isActive: true,
};

const WorkScheduleForm = ({
  workSchedule = null,
  onSubmit,
  onCancel,
  isBusy = false,
  existingDays = [],
  existingDates = [],
}) => {
  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: INITIAL_STATE,
  });

  const scheduleType = watch("scheduleType");
  const isEditing = !!workSchedule;
  const isEditingFlexible = isEditing && workSchedule.scheduleType === "Flexible";

  // Reset form when editing an existing schedule
  useEffect(() => {
    if (workSchedule) {
      reset({
        scheduleType: workSchedule.scheduleType || "Fixed",
        day: workSchedule.day || "",
        date: workSchedule.date ? new Date(workSchedule.date).toISOString().split("T")[0] : "",
        startTime: workSchedule.startTime || "",
        endTime: workSchedule.endTime || "",
        isActive: workSchedule.isActive !== undefined ? workSchedule.isActive : true,
      });
    }
  }, [workSchedule, reset]);

  // Filter available days based on scheduleType
  const availableDays = scheduleType === "Fixed" && workSchedule
    ? weekdays.filter((day) => day === workSchedule.day || !existingDays.includes(day))
    : weekdays.filter((day) => !existingDays.includes(day));

  const onFormSubmit = (data) => {
    // Ensure only relevant field (day or date) is sent based on scheduleType
    const submitData = {
      scheduleType: data.scheduleType,
      ...(data.scheduleType === "Fixed" ? { day: data.day } : { date: isEditingFlexible ? workSchedule.date : data.date }),
      startTime: data.startTime,
      endTime: data.endTime,
      isActive: data.isActive,
    };
    onSubmit(submitData);
    reset(INITIAL_STATE);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onFormSubmit)} padding={3}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            name="scheduleType"
            control={control}
            rules={{ required: "Schedule Type is required" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                select
                fullWidth
                label="Schedule Type"
                {...field}
                error={!!error}
                helperText={error ? error.message : ""}
              >
                <MenuItem value="Fixed">Fixed</MenuItem>
                <MenuItem value="Flexible">Flexible</MenuItem>
              </TextField>
            )}
          />
        </Grid>
        {scheduleType === "Fixed" ? (
          <Grid item xs={12}>
            <Controller
              name="day"
              control={control}
              rules={{ required: "Day is required for Fixed schedule" }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  select
                  fullWidth
                  label="Day"
                  {...field}
                  error={!!error}
                  helperText={error ? error.message : ""}
                >
                  {availableDays.map((day) => (
                    <MenuItem key={day} value={day}>
                      {day}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
        ) : !isEditingFlexible ? (
          <Grid item xs={12}>
            <Controller
              name="date"
              control={control}
              rules={{ required: "Date is required for Flexible schedule" }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  {...field}
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error ? error.message : ""}
                />
              )}
            />
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <Controller
            name="startTime"
            control={control}
            rules={{ required: "Start Time is required" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                {...field}
                InputLabelProps={{ shrink: true }}
                error={!!error}
                helperText={error ? error.message : ""}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="endTime"
            control={control}
            rules={{ required: "End Time is required" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                fullWidth
                label="End Time"
                type="time"
                {...field}
                InputLabelProps={{ shrink: true }}
                error={!!error}
                helperText={error ? error.message : ""}
              />
            )}
          />
        </Grid>
        {workSchedule && (
          <Grid item xs={6}>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => <AppSwitch {...field} label="Is Active?" />}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={onCancel} disabled={isBusy}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" disabled={isBusy}>
              {isBusy ? "Processing..." : "Submit"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WorkScheduleForm;